import "./tabs-container.scss";
import { HTMLProps, useState } from "react";
import { CorrespondenceTab } from "../correspondence-tab/correspondence-tab";
import { FinanceTab } from "../finance-tab/finance-tab";
import { CompletionTab } from "../completion-tab/completion-tab";
import { SolicitorCorrespondenceResponse } from "../../../../../models/classes/solicitor/SolicitorCorrespondenceResponse.class";
import LodgementTab from "../lodgement-tab/lodgement-tab";
import { P2Alert, P2Button, P2ButtonGroup } from "react-bootstrap-common-script";
import { useAppSelector } from "../../../../../redux/hook";
import { LoginSolicitorType } from "../../../../../models/enum/login-solicitor-type.enum";
import { TabNames } from "./tab-names.enum";
import { useGetSolicitorCorrespondenceDetailsQuery } from "../../../../../redux/services/getSolicitor";
import { useSolicitorCaseDetailsContext } from "../../../../../context/solicitor-case-details-context";
import { SolicitorFinancialStatusEnum } from "../../../../../models/enum/solicitor-financial-status.enum";
import moment from "moment";

export interface Tab {
  refetch: () => void;
}

export interface CorrespondenceTabInterface extends Tab {
  correspondenceData?: SolicitorCorrespondenceResponse;
}

interface TabsContainerProps extends HTMLProps<HTMLDivElement> {
  loginTypeArray: LoginSolicitorType[];
}

export function TabsContainer({ ...props }: TabsContainerProps) {
  // States
  const queryParameters = new URLSearchParams(window.location.search);
  const applicationUuid = queryParameters.get("applicationUuid");
  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const { uen } = useAppSelector((state) => state.auth.AuthenticationSlice.corpPassDetails);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);

  const [activeTabName, setActiveTabName] = useState<TabNames>(TabNames.CORRESPONDENCE);

  const {
    solicitorCaseSummaryData,
    solicitorCompletionData,
    isShowingCompletion,
    isShowingLodgement,
    solicitorNotificationData,
    solicitorCompletionIsSuccess,
    solicitorCompletionIsFetching,
    solicitorCompletionRefetch,
  } = useSolicitorCaseDetailsContext();

  const {
    data: correspondenceData,
    isFetching: correspondenceIsFetching,
    isSuccess: correspondenceIsSuccess,
    isError: correspondenceIsError,
    refetch: correspondenceRefetch,
  } = useGetSolicitorCorrespondenceDetailsQuery(
    { uinfin: loginUser?.uinfin ?? "", singpass_token: singpassToken ?? "", application_uuid: applicationUuid ?? "", uen },
    { skip: !loginUser || !singpassToken || !applicationUuid }
  );

  // Derived values
  const numOfCorrespondenceItems = (correspondenceData?.message_info.length ?? 0) + (correspondenceData?.folders.length ?? 0);
  const hasContent = isShowingCompletion || isShowingLodgement;

  function createHandleSelectTab(targetTabName: TabNames) {
    return () => {
      setActiveTabName(targetTabName);
    };
  }

  return (
    <div className="polymer-tabs-container" {...props}>
      <div className="polymer-tabs-container__title-container">
        <button
          className={`polymer-tabs-container__title ${activeTabName === "Correspondence" ? "polymer-tabs-container__title-active" : ""}`}
          key="Correspondence"
          onClick={createHandleSelectTab(TabNames.CORRESPONDENCE)}
        >
          <span>Correspondence</span>
          {numOfCorrespondenceItems !== 0 ? <div className="polymer-tabs-container__title-number">{numOfCorrespondenceItems}</div> : null}
        </button>
        <button
          className={`polymer-tabs-container__title ${activeTabName === "Finance" ? "polymer-tabs-container__title-active" : ""}`}
          key="Finance"
          onClick={createHandleSelectTab(TabNames.FINANCE)}
        >
          <span>Finance</span>
          {solicitorNotificationData?.financial_status === SolicitorFinancialStatusEnum.financial_plan_received ? (
            <div className="polymer-tabs-container__title-number">{1}</div>
          ) : null}
        </button>
        <button
          className={`polymer-tabs-container__title ${activeTabName === "Completion" ? "polymer-tabs-container__title-active" : ""}`}
          key="Completion"
          onClick={createHandleSelectTab(TabNames.COMPLETION)}
        >
          <span>Completion</span>
          {hasContent ? <div className="polymer-tabs-container__title-number">{1}</div> : null}
        </button>
      </div>
      <div className="polymer-tabs-container__content-container">
        <div className={`polymer-tabs-container__content ${activeTabName === "Correspondence" ? "polymer-tabs-container__content-active" : ""}`}>
          <CorrespondenceTab correspondenceData={correspondenceData} isSuccess={correspondenceIsSuccess} refetch={correspondenceRefetch} />
        </div>
        <div className={`polymer-tabs-container__content ${activeTabName === "Finance" ? "polymer-tabs-container__content-active" : ""}`}>
          <FinanceTab refetch={() => {}} />
        </div>
        <div className={`polymer-tabs-container__content ${activeTabName === "Completion" ? "polymer-tabs-container__content-active" : ""}`}>
          {solicitorCompletionIsFetching && (
            <div style={{ paddingLeft: 24, paddingRight: 24 }}>
              <h2 className="polymer-completion-tab__title">Completion Details</h2>
              <p>Loading completion details...</p>
            </div>
          )}
          {!solicitorCompletionIsFetching && !solicitorCompletionIsSuccess && (
            <div style={{ paddingLeft: 24, paddingRight: 24 }}>
              <h2 className="polymer-completion-tab__title">Completion Details</h2>
              <P2Alert
                variant="error"
                text="Unable to load completion details. Please refresh the page or try again."
              />
              <P2ButtonGroup>
                <P2Button variant={"outline-primary"} onClick={() => solicitorCompletionRefetch()}>
                  Retry
                </P2Button>
              </P2ButtonGroup>
            </div>
          )}
          {!solicitorCompletionIsFetching && solicitorCompletionIsSuccess && !hasContent && (
            <div style={{ paddingLeft: 24, paddingRight: 24 }}>
              <h2 className="polymer-completion-tab__title">Completion Details</h2>
              <P2Alert variant="warning" text="There is no actions required at the moment, will let you know if there are any updates on the application." />
            </div>
          )}
          {isShowingCompletion && <CompletionTab />}
          {isShowingLodgement && <LodgementTab />}
        </div>
      </div>
    </div>
  );
}
